import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { 
  FileText, 
  Download, 
  FileSpreadsheet,
  FileJson,
  Eye,
  Search,
  Calendar
} from 'lucide-react';
import { reportsApi, inspectionsApi } from '../services/api';
import { Inspection } from '../types';
import toast from 'react-hot-toast';

const Reports: React.FC = () => {
  const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: inspections, isLoading } = useQuery('completedInspections', () =>
    inspectionsApi.getAll({ status: 'completed' }).then(r => r.data.inspections)
  );

  const { data: reports } = useQuery(
    ['reports', selectedInspection?.id],
    () => selectedInspection ? [{ id: selectedInspection.id, type: 'json' }] : [],
    { enabled: Boolean(selectedInspection) }
  );

  const handleGenerateReport = async (format: 'pdf' | 'json') => {
    if (!selectedInspection) return;
    
    try {
      const response = await reportsApi.generate(selectedInspection.id, format);
      
      if (format === 'pdf' && response.data.downloadUrl) {
        window.open(`http://localhost:3001${response.data.downloadUrl}`, '_blank');
      } else if (format === 'json') {
        const dataStr = JSON.stringify(response.data.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report-${selectedInspection.id}.json`;
        a.click();
      }
      
      toast.success(`${format.toUpperCase()} report generated`);
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  const filteredInspections = inspections?.filter((i: Inspection) =>
    i.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-600">Generate and download inspection reports</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Inspections List */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-gray-900">Select Inspection</h2>
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search inspections..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm"
              />
            </div>
          </div>
          
          <div className="max-h-96 overflow-auto">
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : filteredInspections?.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No completed inspections found
              </div>
            ) : (
              <div className="divide-y">
                {filteredInspections?.map((inspection: Inspection) => (
                  <button
                    key={inspection.id}
                    onClick={() => setSelectedInspection(inspection)}
                    className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                      selectedInspection?.id === inspection.id ? 'bg-primary-50 border-l-4 border-primary-600' : ''
                    }`}
                  >
                    <p className="font-medium text-gray-900">{inspection.title}</p>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(inspection.completedAt!).toLocaleDateString()}
                      </span>
                      {inspection.score !== null && (
                        <span className="text-primary-600">
                          Score: {inspection.score}/{inspection.maxScore}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Report Generation */}
        <div className="lg:col-span-2 space-y-6">
          {selectedInspection ? (
            <>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Generate Report
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button
                    onClick={() => handleGenerateReport('pdf')}
                    className="flex flex-col items-center p-6 border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-3">
                      <FileText className="w-6 h-6 text-red-600" />
                    </div>
                    <span className="font-medium">PDF Report</span>
                    <span className="text-sm text-gray-500 mt-1">Professional format</span>
                  </button>
                  
                  <button
                    onClick={() => handleGenerateReport('json')}
                    className="flex flex-col items-center p-6 border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                      <FileJson className="w-6 h-6 text-blue-600" />
                    </div>
                    <span className="font-medium">JSON Export</span>
                    <span className="text-sm text-gray-500 mt-1">Raw data</span>
                  </button>
                  
                  <button
                    className="flex flex-col items-center p-6 border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-colors opacity-50 cursor-not-allowed"
                    disabled
                  >
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                      <FileSpreadsheet className="w-6 h-6 text-green-600" />
                    </div>
                    <span className="font-medium">CSV Export</span>
                    <span className="text-sm text-gray-500 mt-1">Coming soon</span>
                  </button>
                </div>
              </div>

              {/* Inspection Summary */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Inspection Summary</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Title</p>
                    <p className="font-medium">{selectedInspection.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Template</p>
                    <p className="font-medium">{selectedInspection.Template?.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Completed</p>
                    <p className="font-medium">
                      {new Date(selectedInspection.completedAt!).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">{selectedInspection.location || 'N/A'}</p>
                  </div>
                  {selectedInspection.score !== null && (
                    <div>
                      <p className="text-sm text-gray-500">Score</p>
                      <p className="font-medium text-primary-600">
                        {selectedInspection.score}/{selectedInspection.maxScore}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Previous Reports */}
              {reports && reports.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Previous Reports</h3>
                  
                  <div className="divide-y">
                    {reports.map((report: any) => (
                      <div key={report.id} className="py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {report.type === 'pdf' ? (
                            <FileText className="w-5 h-5 text-red-500" />
                          ) : (
                            <FileJson className="w-5 h-5 text-blue-500" />
                          )}
                          <div>
                            <p className="font-medium">
                              {report.type.toUpperCase()} Report
                            </p>
                            <p className="text-sm text-gray-500">
                              Generated {new Date(report.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        
                        {report.url && (
                          <a
                            href={`http://localhost:3001${report.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-primary-600 hover:text-primary-700"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">Select an inspection</h3>
              <p className="text-gray-600 mt-2">Choose a completed inspection to generate a report</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;